/* ---------------------------------------------------------------------------
   The Philippines, as far as this application knows it — which is a box.

   READ THIS BEFORE DRAWING A COASTLINE.

   src/data/nepal.ts bundles a 267-point national outline so the register's map
   still says where things are when no tile arrives. The equivalent for the
   Philippines is NOT here, and its absence is deliberate rather than pending.

   Nepal is one polygon. The Philippines is an archipelago of more than seven
   thousand islands whose useful outline at register scale is several thousand
   points across dozens of rings — an order of magnitude more bundle than the
   Nepali ring, for a fallback most sessions never see. And there is a harder
   reason than size: an outline of this country cannot be produced from memory or
   from reasoning. Nepal's was REGENERATED by a script from Natural Earth's
   public-domain 1:10m admin-0 data and the script is in the commit that added
   it. Nobody has run that script for the Philippines. A hand-drawn substitute
   would be a national border invented by a program and then printed over a
   government's own bridge register, which is the one thing this file exists to
   prevent.

   So `TenantGeography.outline` is null for `ph-dpwh`, the map draws the extent
   and the structures instead of a fictional coastline, and it says on screen
   that it is doing so. When somebody runs the Natural Earth extraction for
   PH — the largest rings only, Luzon, Mindanao, Samar, Negros, Panay, Palawan,
   Mindoro, Leyte, Bohol, Cebu, Masbate — this is the file it lands in, the same
   provenance paragraph gets written above it, and the profile stops saying null.
   That item is `GR-31` in ROADMAP.md.

   WHAT IS HERE is the national extent. Widely published, unremarkable, and
   enough for the two jobs bounds actually does: give the map a home view at the
   right scale, and give `npm test` a box to reject a transposed or
   sign-flipped coordinate against. Rounded to one decimal place OUTWARD, so it
   is a box that certainly contains the country rather than one that clips it:

     · north  Y'Ami Island, the northern tip of the Batanes group  ~21.4°N
     · south  the Sibutu/Saluag group off south-western Tawi-Tawi   ~4.4°N
     · west   the western edge of Palawan and the Kalayaan group  ~116.9°E
     · east   the Pacific coast of eastern Mindanao and Samar     ~126.7°E

   It is a bounding box at national scale and nothing more. It is NOT a boundary,
   it does not describe territorial waters or any exclusive economic zone, it
   must not be used to decide what is inside the Philippines, and it takes no
   position on any maritime claim — the Kalayaan longitude above is the western
   edge of a box, not an assertion about anything in it.
   --------------------------------------------------------------------------- */

import type { GeoExtent } from '../tenant/types.ts';

/**
 * The archipelago's extent, as the note above gives it.
 *
 * Stated rather than computed, for the same reason `NEPAL_BOUNDS` is: it is the
 * map's home view and the box every coordinate in the register is checked
 * against, and both wanted a constant to be read against rather than a value
 * derived from the very data being verified. There is no outline here to derive
 * it from anyway.
 */
export const PHILIPPINES_BOUNDS: GeoExtent = {
  minLat: 4.4,
  maxLat: 21.4,
  minLng: 116.9,
  maxLng: 126.7,
};

/**
 * The corridor the Daang Maharlika register actually sits in.
 *
 * NOT the tenant's `bounds` — DPWH is a national department and a coordinate
 * outside this corridor is a valid Philippine bridge, not an error. This is the
 * map's opening view, so a register of 106 structures on three highway sections
 * does not open zoomed out across two thousand kilometres of ocean with every
 * pin in one corner.
 *
 * Derived from where those three sections are, not from the register: the
 * records carry no coordinates yet (see src/data/dpwh.ts), so there is nothing
 * to fit. Once they do, the map's own `fitHome` will fit them and this becomes
 * the fallback for a register that has not been located yet.
 */
export const DMRP_CORRIDOR: GeoExtent = {
  minLat: 8.0,
  maxLat: 14.2,
  minLng: 121.8,
  maxLng: 126.2,
};
